import {
  ApolloClient,
  ApolloProvider,
  createHttpLink,
  DocumentNode,
  from,
  InMemoryCache,
  type ServerError,
  ServerParseError,
} from "@apollo/client";

import { createFragmentRegistry } from "@apollo/client/cache";
import type { GraphQLErrors } from "@apollo/client/errors";
import { onError } from "@apollo/client/link/error";
import { createPersistedQueryLink } from "@apollo/client/link/persisted-queries";
import { generatePersistedQueryIdsFromManifest } from "@apollo/persisted-query-lists";
import * as Sentry from "@sentry/react";
import { withScalars } from "apollo-link-scalars";
import { buildClientSchema, OperationDefinitionNode, stripIgnoredCharacters } from "graphql";
import { prop } from "ramda";
import { isNotNil, isNotNilOrEmpty } from "ramda-adjunct";
import React, { FC, PropsWithChildren, useMemo } from "react";
import { persistedQueryManifest, schema } from "../entities";
import { API_ROOT } from "../env";
import { graphqlErrorCodes, graphqlErrorLevel } from "../tools/apolloMiddleware/graphqlErrorCodes";
import { GraphqlErrorContext, rememberGraphqlErrorContext } from "../tools/apolloMiddleware/graphqlErrorContext";
import { invalidPersistedQueriesErrorLink, requestClosureGuardLink } from "../tools/apolloMiddleware/outdatedClient";
import { removeTypenameFromMutationLink } from "../tools/apolloMiddleware/removeTypenameFromMutation";
import { typeMap } from "../tools/apolloMiddleware/scalarTypesParser";
import { useSettings } from "./AppSettings";
import { OutdatedClientDialog } from "@sinch/core/components/OutdatedClientDialog";
import { typePolicies } from "@sinch/core/config/typePolicies";

const schemaDef = buildClientSchema(schema);

// Register every `fragments.graphql` file in the project, not just the central one,
// so shared fragments co-located with components resolve at runtime. Mirrors the codegen
// config, which scans `src/**/fragments.graphql`.
const fragmentModules = import.meta.glob<{ default: DocumentNode }>("/src/**/fragments.graphql", {
  eager: true,
});
const fragmentDocuments = Object.values(fragmentModules).map((module) => module.default);

export const useSinchApolloClient = () => {
  const { timeZone } = useSettings();

  const httpLink = createHttpLink({
    uri: API_ROOT,
    print: (ast, originalPrint) =>
      import.meta.env.MODE !== "production" ? originalPrint(ast) : stripIgnoredCharacters(originalPrint(ast)),
  });

  const persistedQueryLink = createPersistedQueryLink({
    ...generatePersistedQueryIdsFromManifest({
      loadManifest: () => persistedQueryManifest,
    }),
    // Never fall back to sending the query text: the API accepts persisted queries only and answers a request
    // carrying a `query` with an HTTP 400. An unknown id means the bundle is outdated, which is what
    // `invalidPersistedQueriesErrorLink` handles.
    disable: () => false,
    retry: () => false,
  });

  const link = from([
    requestClosureGuardLink,
    withScalars({
      schema: schemaDef,
      typesMap: typeMap(timeZone),
    }),
    removeTypenameFromMutationLink,
    errorLink,
    invalidPersistedQueriesErrorLink,
    persistedQueryLink,
    httpLink,
  ]);

  return useMemo(
    () =>
      new ApolloClient({
        cache: new InMemoryCache({
          fragments: createFragmentRegistry(...fragmentDocuments),
          typePolicies: typePolicies,
        }),
        link: link,
        devtools: {
          enabled: import.meta.env.MODE !== "production",
        },
      }),
    [timeZone]
  );
};

/**
 * Provide apollo client context
 */
export const ApolloClientProvider: FC<PropsWithChildren> = ({ children }) => {
  const client = useSinchApolloClient();
  return (
    <ApolloProvider client={client}>
      {children}
      <OutdatedClientDialog />
    </ApolloProvider>
  );
};

interface ErrorsResponse {
  errors?: GraphQLErrors;
}

interface ErrorExtensions {
  extensions: { redirectUri: string } | Record<string, unknown>;
}

// Log any GraphQL errors or network error that occurred
const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  const responseHttpCode = (networkError as ServerParseError | undefined)?.statusCode;
  const errorCodes = graphqlErrorCodes(graphQLErrors);
  const graphqlContext = rememberGraphqlErrorContext(
    {
      httpCode: responseHttpCode,
      errorMessage: networkError?.name,
      operationName: operation.operationName,
      graphqlErrors: isNotNilOrEmpty(graphQLErrors) ? JSON.stringify(graphQLErrors) : null,
      errorCodes: errorCodes,
      queries: operation.query.definitions.map((def) => ({
        type: (def as OperationDefinitionNode).operation,
        query: (def as OperationDefinitionNode).name?.value,
      })),
    } satisfies GraphqlErrorContext,
    [networkError, ...(graphQLErrors ?? [])]
  );

  // send network error to sentry
  if (networkError) {
    networkErrorHandler(networkError as ServerParseError | ServerError, graphqlContext);
  } else
  // send graphql error to sentry
  if (graphQLErrors) {
    Sentry.captureMessage(`Graphql error`, (scope) => {
      scope.setTag("http.status_code", responseHttpCode);
      scope.setTag("graphql.error_code", errorCodeTag(errorCodes));
      scope.setTransactionName(`${operation.operationName}`);
      // an input the API rejected on purpose is shown to the user by the view, it is not an application error
      scope.setLevel(graphqlErrorLevel(errorCodes));
      scope.setContext("Graphql", graphqlContext);
      return scope;
    });
  }
});

/**
 * Codes of a response as a single Sentry tag value, so failures can be grouped and filtered by them
 */
const errorCodeTag = (errorCodes: string[]) => (errorCodes.length > 0 ? errorCodes.join(",") : undefined);

/**
 * Handles network errors by dispatching them to specific error handlers based on their status code.
 *
 * This function checks the status code of a network error and directs it to the appropriate handler.
 * - Handles 401 Unauthorized error to manage authentication-related issues.
 * - For all other errors, it manage general network errors and send event to Sentry.
 *
 */
const networkErrorHandler = (networkError: ServerParseError | ServerError, context: GraphqlErrorContext) => {
  switch (networkError.statusCode) {
    case 401:
      handleUnauthenticatedError(networkError);
      break;
    default:
      handleUnspecifiedError(networkError, context);
  }
};

/**
 * Handles 401 Unauthorized error by redirecting the user to the login page.
 */
const handleUnauthenticatedError = (serverError: ServerParseError | ServerError) => {
  const responseErrors = prop<ErrorsResponse>("result", serverError)?.errors;
  const redirectError = responseErrors?.find(({ extensions }: ErrorExtensions) =>
    isNotNil(prop("redirectUri", extensions))
  )?.extensions.redirectUri as string | null;
  globalThis.location.href = redirectError || globalThis.location.href;
};

/**
 * Handles unspecified errors by sending them to Sentry.
 */
const handleUnspecifiedError = (networkError: ServerParseError | ServerError, context: GraphqlErrorContext) => {
  const serverError = networkError as ServerParseError;
  Sentry.captureException(networkError, (scope) => {
    scope.setContext("response", {
      type: serverError.name ?? null,
      statusCode: serverError.statusCode ?? null,
      body: serverError.bodyText ?? null,
    });
    // a non 2xx response can still carry coded GraphQL errors, for example a malformed request
    scope.setTag("graphql.error_code", errorCodeTag(context.errorCodes));
    scope.setLevel(graphqlErrorLevel(context.errorCodes));
    scope.setContext("Graphql", context);
    return scope;
  });
};
